import { z } from 'zod';
import log from '~/utils/log';

/**
 * The Store API types define the options of a cart line item as `PropertyGroupOption`.
 * At runtime, the cart payload contains only text values, for example `{ group: 'Size', option: 'XL' }`.
 */
const cartLineItemOptionsSchema = z
    .array(
        z.object({
            group: z.string(),
            option: z.string()
        })
    )
    // A product variant has only a few property groups. The limit prevents the processing of a very large payload.
    .max(50);

export type CartLineItemOption = z.infer<typeof cartLineItemOptionsSchema>[number];

interface LineItemWithPayload {
    readonly payload?: object | null;
}

/**
 * The payload of a line item is optional. A line item without a payload is not a promotion.
 */
export function isPromotionLineItem(lineItem: LineItemWithPayload): boolean {
    return !!lineItem.payload && 'promotionId' in lineItem.payload;
}

export function parseCartLineItemOptions(options: unknown): CartLineItemOption[] {
    const result = cartLineItemOptionsSchema.safeParse(options);

    if (!result.success) {
        log.warn('[cartLineItem] Ignoring line item options with an unexpected shape', result.error.issues);
        return [];
    }

    return result.data;
}
